import {Alert, Button, DatePicker, Form, Input, Modal, Select, Space, Switch, Table, Tag, message} from "antd";
import {CategoryDto, DiscountCodeCreateDto, DiscountCodeUpdateDto, DiscountType, ProductDto} from "../../../api";
import {CrudForm, CrudFormItemProps, nameof} from "digimuth-components/dist";
import React, {useState} from "react";
import {RangePickerProps} from "antd/lib/date-picker";
import {discountTypeTranslation} from "../../../helpers/DiscountTypeHelper";
import dayjs from "dayjs";

const {Option} = Select;
const bulkCountFieldName = "bulkCount";
const bulkPrefixFieldName = "bulkPrefix";
const bulkLengthFieldName = "bulkLength";
const bulkIncludeUppercaseFieldName = "bulkIncludeUppercase";
const bulkIncludeDigitsFieldName = "bulkIncludeDigits";

export default function DiscountCodeModalForm(props: DiscountCodeModalProps) {
    const [selectedDiscountType, setSelectedDiscountType] = useState<DiscountType | null>(props.initialValues?.discountType ?? null);
    const [discountCode, setDiscountCode] = useState<string>(props.initialValues?.code ?? "");
    const [form] = Form.useForm();
    const [isBulkSectionVisible, setIsBulkSectionVisible] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewCodes, setPreviewCodes] = useState<string[]>([]);
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const watchedDiscountType = Form.useWatch(nameof<DiscountCodeCreateDto>("discountType"), form) ?? selectedDiscountType;
    const watchedDiscountValue = Number(Form.useWatch(nameof<DiscountCodeCreateDto>("discountValue"), form) ?? 0);
    const watchedMinimumOrderAmountValue = Form.useWatch(nameof<DiscountCodeCreateDto>("minimumOrderAmount"), form);
    const watchedMinimumOrderAmount = watchedMinimumOrderAmountValue === undefined || watchedMinimumOrderAmountValue === null || watchedMinimumOrderAmountValue === ""
        ? null
        : Number(watchedMinimumOrderAmountValue);
    const canCreateFreeOrder =
        watchedDiscountType === DiscountType.Percent && watchedDiscountValue >= 100
        || watchedDiscountType === DiscountType.Quota && watchedDiscountValue > 0
        && (watchedMinimumOrderAmount === null || watchedMinimumOrderAmount < watchedDiscountValue);

    const onRandomizeCodeClick = () => {
        const randomCode = Math.random().toString(36).substring(2, 10).toUpperCase();
        form.setFieldsValue({"code": randomCode});
        setDiscountCode(randomCode);
    };

    const handleInputChange = (event: any) => {
        setDiscountCode(event.target.value);
    };

    const disabledDate: RangePickerProps["disabledDate"] = (current) => {
        return current && current < dayjs().startOf("day");
    };

    const initialValues = {
        ...props.initialValues,
        expirationDate: props.initialValues ? dayjs(props.initialValues?.expirationDate) : null,
        applicableProductIds: props.initialValues?.applicableProductIds ?? [],
        applicableCategoryIds: props.initialValues?.applicableCategoryIds ?? [],
        [bulkCountFieldName]: 10,
        [bulkPrefixFieldName]: "",
        [bulkLengthFieldName]: 8,
        [bulkIncludeUppercaseFieldName]: true,
        [bulkIncludeDigitsFieldName]: true
    };

    const bulkFormItems: CrudFormItemProps[] = !props.allowBulkCreate || !isBulkSectionVisible ? [] : [
        {
            name: bulkCountFieldName,
            label: "Liczba kodów",
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {
                    validator: (_: any, value: any) => {
                        const parsedValue = Number(value);
                        if (!Number.isInteger(parsedValue) || parsedValue < 1 || parsedValue > 500) {
                            return Promise.reject(new Error("Podaj liczbę od 1 do 500"));
                        }

                        return Promise.resolve();
                    }
                }
            ],
            children: <Input type="number" min={1} max={500}/>
        },
        {
            name: bulkPrefixFieldName,
            label: "Prefix",
            rules: [
                {
                    validator: (_: any, value: any) => {
                        if (!value) {
                            return Promise.resolve();
                        }

                        return /^[a-zA-Z0-9-]+$/.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error("Prefix może zawierać tylko litery, cyfry i myślnik"));
                    }
                }
            ],
            children: <Input placeholder="np. VIP-"/>
        },
        {
            name: bulkLengthFieldName,
            label: "Liczba losowanych znaków",
            rules: [
                {required: true, message: "To pole jest wymagane"},
                {
                    validator: (_: any, value: any) => {
                        const parsedValue = Number(value);
                        if (!Number.isInteger(parsedValue) || parsedValue < 1 || parsedValue > 24) {
                            return Promise.reject(new Error("Podaj liczbę od 1 do 24"));
                        }

                        return Promise.resolve();
                    }
                }
            ],
            children: <Input type="number" min={1} max={24}/>
        },
        {
            name: bulkIncludeUppercaseFieldName,
            label: "Wielkie litery",
            valuePropName: "checked",
            children: <Switch/>
        },
        {
            name: bulkIncludeDigitsFieldName,
            label: "Cyfry",
            valuePropName: "checked",
            children: <Switch/>
        }
    ];

    const formItems: CrudFormItemProps[] = [
        ...(!isBulkSectionVisible ? [{
            name: nameof<DiscountCodeCreateDto>("code"),
            label: "Kod rabatowy",
            rules: [
                {
                    required: true,
                    message: "Kod rabatowy może zawierać tylko litery, cyfry oraz myślniki",
                    pattern: new RegExp(/^[a-zA-Z0-9-]+$/)
                }
            ],
            children: <div style={{display: "flex"}}>
                <Input onChange={handleInputChange} value={discountCode}/>
                <Button style={{marginLeft: "25px"}} onClick={onRandomizeCodeClick}>
                    Wylosuj kod
                </Button>
            </div>
        }] : []), {
            name: nameof<DiscountCodeCreateDto>("expirationDate"),
            label: "Data ważności",
            children: <DatePicker
                showTime={{format: "HH:mm", minuteStep: 5, showSecond: false}}
                format="YYYY-MM-DD HH:mm"
                disabledDate={disabledDate}
            />
        }, {
            name: nameof<DiscountCodeCreateDto>("discountType"),
            label: "Typ obniżki",
            rules: [
                {required: true, message: "To pole jest wymagane"}
            ],
            children: <Select onChange={setSelectedDiscountType}>
                {Object.values(DiscountType).filter(type => type !== DiscountType.BlackFriday).map((type, index) => <Option value={type} key={index}>
                    {discountTypeTranslation(type)?.translation}
                </Option>)}
            </Select>
        },
        ...((watchedDiscountType === DiscountType.Quota || watchedDiscountType === DiscountType.Percent) ? [{
            name: nameof<DiscountCodeCreateDto>("discountValue"),
            label: `${watchedDiscountType === DiscountType.Percent ? "Procent obniżki" : "Kwota obniżki"}`,
            rules: [
                {required: true, message: "To pole jest wymagane"},
                ...((watchedDiscountType === DiscountType.Percent) ? [{
                    validator: (_: any, value: any) => {
                        if (value === undefined || value === null || value === "") {
                            return Promise.resolve();
                        }

                        return Number(value) <= 100
                            ? Promise.resolve()
                            : Promise.reject(new Error("Procent obniżki nie może przekraczać 100"));
                    }
                }] : [])
            ],
            children: <Input type={"number"} max={watchedDiscountType === DiscountType.Percent ? 100 : undefined}/>
        }] : []), {
            name: nameof<DiscountCodeCreateDto>("minimumOrderAmount"),
            label: "Minimalna kwota zamówienia",
            children: <Input type={"number"}/>
        }, ...((canCreateFreeOrder) ? [{
            name: "freeOrderWarning",
            label: "Uwaga",
            children: <Alert
                type="warning"
                showIcon
                message="Ten kod może obniżyć wartość zamówienia do 0 zł. W takim przypadku zamówienie zostanie złożone bez płatności."
            />
        }] : []), {
            name: nameof<DiscountCodeCreateDto>("minimumOrderAmountOnlyForApplicableProducts"),
            label: "Minimum tylko dla objętych produktów",
            valuePropName: "checked",
            children: <div>
                <Switch/>
                <Alert
                    style={{marginTop: "10px"}}
                    type="info"
                    showIcon
                    message="Gdy włączone, minimalna kwota zamówienia liczona jest tylko z produktów i kategorii objętych kodem. Gdy wyłączone, liczona jest z całego zamówienia."
                />
            </div>
        }, {
            name: nameof<DiscountCodeCreateDto>("applicableProductIds"),
            label: "Produkty objęte kodem",
            children: <Select mode={"multiple"} showSearch optionFilterProp={"label"}>
                {props.products?.map(product => <Option key={product.id} value={product.id} label={product.name}>
                    {product.name}
                </Option>)}
            </Select>
        }, {
            name: nameof<DiscountCodeCreateDto>("applicableCategoryIds"),
            label: "Kategorie objęte kodem",
            children: <Select mode={"multiple"} showSearch optionFilterProp={"label"}>
                {props.categories?.map(category => <Option key={category.id} value={category.id} label={category.name}>
                    {category.name}
                </Option>)}
            </Select>
        }, {
            name: nameof<DiscountCodeCreateDto>("usageLimit"),
            label: "Limit wykorzystań",
            children: <Input type={"number"} min={1}/>
        },
        ...bulkFormItems
    ];

    const toggleBulkSection = () => {
        setIsBulkSectionVisible(currentValue => {
            const nextValue = !currentValue;

            if (nextValue) {
                const currentCode = `${form.getFieldValue(nameof<DiscountCodeCreateDto>("code")) ?? discountCode ?? ""}`.trim();
                if (currentCode) {
                    form.setFieldValue(bulkPrefixFieldName, currentCode);
                }
            }

            return nextValue;
        });
    };

    const buildPreviewCodes = (values: any) => {
        const prefix = `${values[bulkPrefixFieldName] ?? ""}`;
        const count = Number(values[bulkCountFieldName]);
        const length = Number(values[bulkLengthFieldName]);
        const characters = [
            values[bulkIncludeUppercaseFieldName] ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ" : "",
            values[bulkIncludeDigitsFieldName] ? "0123456789" : ""
        ].join("");

        if (!characters.length) {
            throw new Error("Wybierz przynajmniej jeden typ znaków");
        }

        const maxCombinations = Math.pow(characters.length, length);
        if (count > maxCombinations) {
            throw new Error("Dla wybranego formatu nie da się wygenerować tylu unikalnych kodów");
        }

        const generatedCodes = new Set<string>();
        let attempts = 0;
        const maxAttempts = Math.max(count * 20, 1000);

        while (generatedCodes.size < count) {
            const randomPart = Array.from({length}, () => characters[Math.floor(Math.random() * characters.length)]).join("");
            generatedCodes.add(`${prefix}${randomPart}`);
            attempts++;

            if (attempts > maxAttempts) {
                throw new Error("Nie udało się wygenerować unikalnych kodów dla wybranego formatu");
            }
        }

        return Array.from(generatedCodes);
    };

    const getDiscountCodePayload = (values: any): DiscountCodeCreateDto => ({
        code: `${values.code ?? ""}`,
        expirationDate: values.expirationDate,
        discountType: values.discountType,
        discountValue: values.discountValue === undefined || values.discountValue === null || values.discountValue === "" ? undefined : Number(values.discountValue),
        minimumOrderAmount: values.minimumOrderAmount === undefined || values.minimumOrderAmount === null || values.minimumOrderAmount === "" ? undefined : Number(values.minimumOrderAmount),
        usageLimit: values.usageLimit === undefined || values.usageLimit === null || values.usageLimit === "" ? undefined : Number(values.usageLimit),
        minimumOrderAmountOnlyForApplicableProducts: !!values.minimumOrderAmountOnlyForApplicableProducts,
        applicableProductIds: values.applicableProductIds ?? [],
        applicableCategoryIds: values.applicableCategoryIds ?? []
    });

    const openBulkPreview = async () => {
        try {
            const values = await form.validateFields();
            const generatedCodes = buildPreviewCodes(values);
            setPreviewCodes(generatedCodes);
            setIsPreviewModalVisible(true);
        } catch (error: any) {
            if (error?.errorFields?.length) {
                return;
            }

            message.error(error?.message || "Nie udało się wygenerować podglądu kodów");
        }
    };

    const saveBulkCodes = async () => {
        if (!props.onBulkSubmit) {
            return;
        }

        try {
            setIsBulkSaving(true);
            const values = await form.validateFields();
            const result = await props.onBulkSubmit(getDiscountCodePayload(values), previewCodes.length ? previewCodes : buildPreviewCodes(values));

            if (result) {
                message.error(typeof result === "string" ? result : "Nie udało się utworzyć kodów");
                return;
            }

            setIsPreviewModalVisible(false);
        } finally {
            setIsBulkSaving(false);
        }
    };

    const bulkActions = props.allowBulkCreate ? <Space>
        <Button onClick={toggleBulkSection}>
            {isBulkSectionVisible ? "Ukryj masowe tworzenie" : "Utwórz wiele..."}
        </Button>
        {isBulkSectionVisible ? <Button type="default" onClick={openBulkPreview}>
            Podgląd kodów
        </Button> : null}
    </Space> : undefined;

    return <>
        <CrudForm
            formItems={formItems}
            form={form}
            onSubmit={props.onSubmit}
            initialValues={initialValues}
            saveButtonTitle={props.saveButtonTitle}
            submitButtonProps={isBulkSectionVisible ? {style: {display: "none"}} : undefined}
            extraActions={bulkActions}
        />
        <Modal
            open={isPreviewModalVisible}
            title="Podgląd generowanych kodów"
            onCancel={() => setIsPreviewModalVisible(false)}
            onOk={saveBulkCodes}
            okText="Zapisz"
            cancelText="Anuluj"
            confirmLoading={isBulkSaving}
            width={720}
        >
            <Alert
                type="info"
                showIcon
                style={{marginBottom: "16px"}}
                message={`Zostanie utworzonych ${previewCodes.length} kodów.`}
            />
            <Table
                rowKey={(value) => value.code}
                pagination={{pageSize: 10}}
                dataSource={previewCodes.map((code, index) => ({id: index + 1, code}))}
                columns={[
                    {
                        title: "#",
                        key: "index",
                        width: 80,
                        render: (_: any, __: { id: number }, index: number) => index + 1
                    },
                    {
                        title: "Kod",
                        dataIndex: "code",
                        key: "code",
                        render: (code: string) => <Tag>{code}</Tag>
                    }
                ]}
            />
        </Modal>
    </>;
}

interface DiscountCodeModalProps {
    initialValues?: DiscountCodeCreateDto | DiscountCodeUpdateDto,
    onSubmit: (value: DiscountCodeCreateDto | DiscountCodeUpdateDto) => Promise<any>,
    onBulkSubmit?: (value: DiscountCodeCreateDto, codes: string[]) => Promise<any>,
    saveButtonTitle: string,
    products: ProductDto[],
    categories: CategoryDto[],
    allowBulkCreate?: boolean,
}
